import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getBlogPost } from "@/lib/site.functions";
import { articleSearchMetadata } from "@/lib/article-seo";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { findAuthor, authorDisplayName, ANDERS_ID } from "@/lib/authors";
import { NotFoundPage } from "@/components/not-found";
import amsterdamSummitSocial from "@/assets/amsterdam-summit-social.jpg.asset.json";
import dealApprovalImage from "@/assets/salesforce-deal-approval-decision.jpg.asset.json";

// Series definitions. Add new multi-part series here.
type SeriesEpisode = { slug: string; shortTitle: string };
type Series = { name: string; episodes: SeriesEpisode[] };

const SERIES: Series[] = [
  {
    name: "The AI Sourcing Shift in Nordic Procurement",
    episodes: [
      { slug: "ai-procurement-trends-scandinavia", shortTitle: "Redefining Enterprise Efficiency" },
      { slug: "ai-procurement-trends-scandinavia-part-2", shortTitle: "The Cost of Delay & FAQ" },
    ],
  },
  {
    name: "7 SaaS Contract Clauses That Drive Up Salesforce Costs",
    episodes: [
      { slug: "7-saas-contract-clauses-that-drive-up-salesforce-costs", shortTitle: "Clauses 1–4 & Why They Matter" },
      { slug: "7-saas-contract-clauses-that-drive-up-salesforce-costs-part-2", shortTitle: "Bundles, Usage, Audit & Framework" },
    ],
  },
];

function findSeries(slug: string) {
  for (const s of SERIES) {
    const idx = s.episodes.findIndex((e) => e.slug === slug);
    if (idx !== -1) {
      return {
        series: s,
        index: idx,
        episode: s.episodes[idx],
        next: s.episodes[idx + 1] ?? null,
        prev: s.episodes[idx - 1] ?? null,
        total: s.episodes.length,
      };
    }
  }
  return null;
}


const SITE_URL = "https://saased.co";

export const Route = createFileRoute("/blog/$slug")({
  validateSearch: (search: Record<string, unknown>) => {
    // Only keep an explicit ?page=2; never append ?page=1 to clean article URLs.
    const raw = Number(search.page);
    return (raw === 2 ? { page: 2 } : {}) as { page?: 2 };
  },
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["blog-post", params.slug],
      queryFn: () => getBlogPost({ data: { slug: params.slug } }),
    });
    // Missing article → real HTTP 404 via the not-found boundary.
    if (!data?.post) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post as
      | {
          title: string;
          excerpt: string | null;
          cover_image_url: string | null;
          published_at: string;
          updated_at?: string | null;
          author?: string | null;
          meta_description?: string | null;
          content?: string | null;
          format?: string | null;
        }
      | null
      | undefined;
    const url = `${SITE_URL}/blog/${params.slug}`;
    if (!post) {
      // Not found: no canonical; noindex is rendered by NotFoundPage.
      return { meta: [{ title: "Article — SaaSed" }] };
    }
    const description =
      (post.meta_description?.trim() || post.excerpt?.trim() || `${post.title} — SaaSed`).slice(0, 300);
    const searchMetadata = articleSearchMetadata(params.slug, post.title, description);
    const bodyImage = SOCIAL_IMAGE_BY_SLUG[params.slug];
    const image = post.cover_image_url
      ? new URL(post.cover_image_url, `${SITE_URL}/`).href
      : bodyImage
        ? new URL(bodyImage, `${SITE_URL}/`).href
        : undefined;
    const socialImage = params.slug === "what-i-heard-at-my-first-it-procurement-sourcing-summit"
      ? new URL(amsterdamSummitSocial.url, `${SITE_URL}/`).href
      : image;
    const extended = TEXT_BYLINE_SLUGS.has(params.slug);
    const day = post.published_at.slice(0, 10);

    const jsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description,
      datePublished: extended ? day : post.published_at,
      dateModified: post.updated_at ?? post.published_at,
      author: (() => {
        const a = findAuthor(post.author);
        if (a && extended) {
          return {
            "@type": "Person",
            ...(a.slug === "anders-gustafsson" ? { "@id": ANDERS_ID } : {}),
            name: a.name,
            jobTitle: a.role,
            url: `${SITE_URL}/blog/author/${a.slug}`,
            sameAs: [a.linkedin],
            worksFor: { "@type": "Organization", name: "SaaSed", url: SITE_URL },
          };
        }
        return a
          ? { "@type": "Person", ...(a.slug === "anders-gustafsson" ? { "@id": ANDERS_ID } : {}), name: a.name, ...(params.slug === "inside-phmgs-salesforce-renewal-a-conversation-with-cfo-matthew-toynton" ? { jobTitle: a.role } : {}), url: `${SITE_URL}/blog/author/${a.slug}`, sameAs: [a.linkedin] }
          : { "@type": "Organization", name: post.author?.trim() || "SaaSed" };
      })(),
      publisher: {
        "@type": "Organization",
        name: "SaaSed",
        ...(extended ? { url: SITE_URL } : {}),
        logo: { "@type": "ImageObject", url: extended ? `${SITE_URL}/favicon.png` : `${SITE_URL}/favicon.ico` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
    };
    if (image) jsonLd.image = [image];

    // FAQPage is generated for every article whose body has a FAQ section; question and answer text must always match the visible text.
    // FAQPage JSON-LD built from the article's own FAQ section, for every
    // article that has one (both FAQ formats). One block per article, only
    // when at least two Q&A pairs are found.
    const faqScripts: { type: string; children: string }[] = [];
    if (post.content) {
      const faqHtml = post.format === "html" ? post.content : renderMarkdown(post.content);
      const pairs = extractFaqPairs(faqHtml);
      if (pairs && pairs.length >= 2) {
        faqScripts.push({
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            mainEntity: pairs.map((p) => ({
              "@type": "Question",
              name: p.q,
              acceptedAnswer: { "@type": "Answer", text: p.a },
            })),
          }),
        });
      }
    }

    return {
      meta: [
        { title: searchMetadata.title },
        { name: "description", content: searchMetadata.description },
        { name: "author", content: post.author?.trim() || "SaaSed" },
        { property: "og:title", content: searchMetadata.title },
        { property: "og:description", content: searchMetadata.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...(socialImage
          ? [
              { property: "og:image", content: socialImage },
              { name: "twitter:image", content: socialImage },
            ]
          : []),
        { name: "twitter:card", content: socialImage ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: searchMetadata.twitterTitle ?? searchMetadata.title },
        { name: "twitter:description", content: searchMetadata.description },
        { property: "article:published_time", content: post.published_at },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(jsonLd) },
        ...faqScripts,
      ],
    };
  },
  errorComponent: () => (
    <div className="container-x py-24">
      <h1 className="text-3xl">Something went wrong</h1>
      <p className="mt-4 text-muted-foreground">We couldn't load this article.</p>
    </div>
  ),
  notFoundComponent: NotFoundPage,
  component: BlogPost,
});

function BlogPost() {
  const { slug } = Route.useParams();
  const { page } = Route.useSearch();
  const loaderData = Route.useLoaderData();
  const fetchPost = useServerFn(getBlogPost);
  const { data, isLoading } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: () => fetchPost({ data: { slug } }),
    initialData: loaderData,
  });

  if (isLoading) {
    return (
      <div className="container-x py-24">
        <div className="h-8 w-40 animate-pulse rounded bg-secondary" />
        <div className="mt-6 h-12 w-3/4 animate-pulse rounded bg-secondary" />
      </div>
    );
  }

  if (!data?.post) {
    throw notFound();
  }

  const post = data.post as typeof data.post & { format?: string | null };
  const rawHtml = post.format === "html" ? post.content : renderMarkdown(post.content);
  const fullHtml = stripHeroImagePlaceholders(normalizeArticleHtml(rawHtml));
  void page;
  // The page title is the only H1; demote any H1 inside the body to H2 (same look).
  const html = fullHtml
    .replace(/<h1(\s|>)/gi, "<h2 data-demoted-h1$1")
    .replace(/<\/h1>/gi, "</h2>");
  const isLastPage = true;
  const seriesInfo = findSeries(slug);
  const postAuthor = findAuthor(post.author);
  const modifiedDate = post.updated_at ?? post.published_at;
  const showUpdatedDate = new Date(modifiedDate).getTime() - new Date(post.published_at).getTime() >= 86_400_000;

  return (
    <article className="container-x max-w-4xl py-20 md:py-28">
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} /> All insights
      </Link>

      {seriesInfo && (
        <div className="mt-8 rounded-2xl border border-border bg-secondary/40 p-5">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <span className="rounded-full bg-foreground px-2.5 py-1 text-background">Series</span>
            <span className="text-foreground">{seriesInfo.series.name}</span>
            <span>· Part {seriesInfo.index + 1} of {seriesInfo.total}</span>
          </div>
          <ol className="mt-4 space-y-2 text-sm">
            {seriesInfo.series.episodes.map((ep, i) => {
              const current = ep.slug === slug;
              return (
                <li key={ep.slug} className="flex gap-3">
                  <span className={`font-serif text-muted-foreground ${current ? "text-foreground" : ""}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {current ? (
                    <span className="font-medium text-foreground">{ep.shortTitle}</span>
                  ) : (
                    <Link
                      to="/blog/$slug"
                      params={{ slug: ep.slug }}
                      className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      {ep.shortTitle}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      <div className="flex flex-col gap-6 md:gap-8">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="rounded-full bg-primary/40 px-2.5 py-0.5 font-medium text-foreground">{post.category}</span>
          {TEXT_BYLINE_SLUGS.has(slug) ? (
            <time dateTime={post.published_at.slice(0, 10)}>
              {new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}
            </time>
          ) : (
            <span>{new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}</span>
          )}
          {!postAuthor && (
            <>
              <span>·</span>
              <span>{authorDisplayName(post.author)}</span>
            </>
          )}
          {showUpdatedDate && (
            <>
              <span>·</span>
              <time dateTime={modifiedDate}>
                Updated {new Date(modifiedDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}
              </time>
            </>
          )}
        </div>

        <h1 className="font-sans text-3xl font-bold leading-tight whitespace-pre-line md:text-4xl lg:text-5xl">{post.title}</h1>
        {/* Any article whose author matches a named team member gets the linked photo byline. */}
        {postAuthor && (
          <div className="flex items-center gap-4">
            <Link to="/blog/author/$slug" params={{ slug: postAuthor.slug }} className="shrink-0" aria-label={postAuthor.name}>
              <img
                src={postAuthor.photo}
                alt={postAuthor.name}
                width={56}
                height={56}
                className="aspect-square h-14 w-14 rounded-full object-cover object-top"
              />
            </Link>
            <div className="flex flex-col">
              <Link
                to="/blog/author/$slug"
                params={{ slug: postAuthor.slug }}
                className="font-medium text-foreground underline-offset-4 hover:underline"
              >
                {postAuthor.name}
              </Link>
              <span className="text-sm text-muted-foreground">{postAuthor.role}</span>
            </div>
          </div>
        )}
        {post.excerpt?.trim() && (
          <p className="text-xl leading-relaxed text-muted-foreground text-left whitespace-pre-line">{post.excerpt}</p>
        )}

        {post.cover_image_url && (
          <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl bg-secondary">
            <img
              src={post.cover_image_url}
              alt={post.main_image_alt?.trim() || post.title}
              width={1280}
              height={768}
              loading="eager"
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>

      <div
        className="blog-content prose prose-neutral prose-lg mt-10 max-w-none font-sans text-foreground/90 text-left leading-relaxed
          prose-headings:font-sans prose-headings:text-foreground prose-headings:scroll-mt-24
          prose-h1:text-4xl prose-h1:mt-12 prose-h1:mb-6 prose-h1:font-semibold prose-h1:text-left prose-h1:leading-tight
          prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-5 prose-h2:font-semibold prose-h2:text-left prose-h2:leading-snug
          prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4 prose-h3:font-semibold prose-h3:text-left prose-h3:leading-snug
          prose-p:mb-6 prose-p:leading-relaxed
          prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-3
          prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-3
          prose-li:leading-relaxed prose-li:marker:text-foreground/60
          prose-blockquote:my-8 prose-blockquote:border-l-4 prose-blockquote:border-foreground/20 prose-blockquote:pl-6 prose-blockquote:italic
          prose-strong:text-foreground prose-strong:font-semibold
          prose-a:text-[#1a1408] prose-a:underline prose-a:underline-offset-4 prose-a:font-medium hover:prose-a:opacity-70
          [&_a]:text-[#1a1408] [&_a]:underline [&_a]:underline-offset-4
          [&_table]:my-8 [&_table]:block [&_table]:overflow-x-auto [&_table]:w-full [&_table]:text-left [&_table]:text-sm [&_table]:border [&_table]:border-[#f0e8c8] [&_table]:rounded-lg [&_table]:border-separate [&_table]:border-spacing-0
          [&_thead]:bg-[#f0e8c8]
          [&_th]:px-4 [&_th]:py-[14px] [&_th]:font-semibold [&_th]:text-foreground [&_th]:border-b [&_th]:border-[#f0e8c8] [&_th]:text-left [&_th]:align-top
          [&_td]:px-4 [&_td]:py-[14px] [&_td]:border-b [&_td]:border-[#f0e8c8] [&_td]:align-top
          [&_tbody_tr:last-child_td]:border-b-0"
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <RelatedResources currentSlug={slug} />


      {isLastPage && seriesInfo?.next && (
        <Link
          to="/blog/$slug"
          params={{ slug: seriesInfo.next.slug }}
          className="group mt-16 flex flex-col gap-2 rounded-2xl border border-border bg-card p-8 shadow-soft transition-all hover:-translate-y-1 hover:shadow-glow"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Continue reading · Part {seriesInfo.index + 2} of {seriesInfo.total}
          </span>
          <span className="text-2xl leading-snug text-foreground">{seriesInfo.next.shortTitle}</span>
          <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-foreground">
            Read the next part <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        </Link>
      )}

      {isLastPage && seriesInfo && !seriesInfo.next && (
        <div className="mt-16 rounded-2xl border border-border bg-card p-8">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            End of series · {seriesInfo.series.name}
          </span>
          <h3 className="mt-2 text-2xl">That's the full series.</h3>
          <p className="mt-2 text-muted-foreground">Re-read any episode or jump back to the start.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {seriesInfo.series.episodes.map((ep, i) => (
              <Link
                key={ep.slug}
                to="/blog/$slug"
                params={{ slug: ep.slug }}
                className={`rounded-full border border-border px-4 py-1.5 text-xs ${ep.slug === slug ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                Part {i + 1}
              </Link>
            ))}
          </div>
        </div>
      )}

      {isLastPage && (
        <div
          className="mt-10 rounded-2xl bg-secondary/60 p-8"
          style={{ minHeight: "220px", contain: "layout" }}
        >
          <h3 className="text-2xl">Want this kind of intel on your renewal?</h3>
          <p className="mt-2 text-muted-foreground">
            Don’t head into your next software negotiation alone
          </p>
          <Link
            to="/contact"
            search={{ utm_source: "blog", utm_medium: "post-footer", utm_campaign: slug }}
            className="mt-5 inline-flex items-center rounded-full bg-[#000000] px-5 py-2.5 text-sm font-medium text-white"
            style={{ color: "#FFFFFF", backgroundColor: "#000000" }}
          >
            Contact Us
          </Link>
        </div>
      )}
    </article>
  );
}

const SALESFORCE_RESOURCES = [
  {
    slug: "what-is-actually-a-salesforce-sela",
    title: "What is Salesforce SELA?",
  },
  {
    slug: "how-salesforce-sela-pricing-models-affect-renewal-costs",
    title: "How SELA Pricing Impacts Renewals",
  },
  {
    slug: "salesforce-sela-avoiding-bundling-traps-and-securing-decoupled-pricing",
    title: "Avoiding Salesforce Bundling Traps",
  },
  {
    slug: "salesforce-sela-changes-negotiation-tactics-for",
    title: "Salesforce SELA Negotiation Tactics",
  },
];

function RelatedResources({ currentSlug }: { currentSlug: string }) {
  // Only show the curated SELA resources block on SELA-specific posts,
  // i.e. slugs that explicitly reference SELA (or the sibling AELA) —
  // not every post that merely mentions "salesforce".
  const selaSlugs = new Set(SALESFORCE_RESOURCES.map((r) => r.slug));
  const isSelaPost = selaSlugs.has(currentSlug) || /\b(sela|aela)\b/i.test(currentSlug);
  if (!isSelaPost) return null;

  const links = SALESFORCE_RESOURCES.filter((r) => r.slug !== currentSlug);
  if (links.length === 0) return null;

  return (
    <nav
      aria-label="Related resources"
      className="mt-12 rounded-2xl border border-border bg-secondary/40 p-8 md:min-h-[420px] lg:min-h-[420px]"
      style={{ minHeight: "320px", contain: "layout", contentVisibility: "auto", containIntrinsicSize: "420px" }}
    >

      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        Related resources
      </p>
      <h3 className="mt-2 text-2xl text-foreground">Keep reading</h3>
      <ul className="mt-6 space-y-3">
        {links.map((link) => (
          <li key={link.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: link.slug }}
              className="group flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-glow"
            >
              <span className="font-sans text-base font-medium text-foreground">
                {link.title}
              </span>
              <ArrowRight
                size={18}
                className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
                aria-hidden="true"
              />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// Split rendered article HTML into two roughly equal pages by picking the
// <h2> closest to the character midpoint. Returns [page1, page2] where page2
// is null when the article is too short or has no suitable <h2> split point.
function splitHtmlIntoTwoPages(html: string): [string, string | null] {
  if (!html || html.length < 2500) return [html, null];
  const regex = /<h2\b[^>]*>/gi;
  const positions: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = regex.exec(html)) !== null) positions.push(m.index);
  if (positions.length < 2) return [html, null];
  const mid = html.length / 2;
  // Skip the very first h2 so page 1 isn't empty.
  const candidates = positions.slice(1);
  let best = candidates[0];
  for (const p of candidates) {
    if (Math.abs(p - mid) < Math.abs(best - mid)) best = p;
  }
  const page1 = html.slice(0, best).trim();
  const page2 = html.slice(best).trim();
  if (!page1 || !page2) return [html, null];
  return [page1, page2];
}

// Normalize HTML coming from the webhook:
// - Rewrite internal saased.co links to relative paths (so they stay in-app
//   and don't open in a new tab).
// - Rewrite bare homepage links (https://saased.co or https://saased.co/) to
//   /contact — those are CTA links pointing at the contact section.
// - Ensure external links keep target=_blank rel=noopener.
function normalizeArticleHtml(html: string): string {
  if (!html) return html;
  return html.replace(/<a\b([^>]*?)href=(["'])([^"']+)\2([^>]*)>/gi, (_m, pre, q, href, post) => {
    let newHref = href as string;
    let attrs = `${pre}${post}`;

    // Strip target/rel on internal links; we'll re-add for external.
    const stripTargetRel = () => {
      attrs = attrs
        .replace(/\s*target=(["'])[^"']*\1/gi, "")
        .replace(/\s*rel=(["'])[^"']*\1/gi, "");
    };

    try {
      const u = new URL(newHref, "https://saased.co");
      const isInternal = u.hostname === "saased.co" || u.hostname === "www.saased.co";
      if (isInternal) {
        const path = u.pathname || "/";
        // Bare homepage link → contact (CTA pattern from BlogSEO).
        newHref = path === "/" ? "/contact" : `${path}${u.search}${u.hash}`;
        stripTargetRel();
      } else if (/^https?:/i.test(newHref)) {
        // External link — make sure it's safe & opens new tab.
        stripTargetRel();
        attrs += ` target="_blank" rel="noopener noreferrer"`;
      }
    } catch {
      // leave as-is
    }

    return `<a${attrs} href=${q}${newHref}${q}>`;
  });
}

// Strip editorial hero-image placeholder captions that sometimes arrive
// in article content. These should never render under the cover image.
function stripHeroImagePlaceholders(html: string): string {
  return html.replace(/<blockquote\b[^>]*>[\s\S]*?<\/blockquote>/gi, (match) =>
    match.toLowerCase().includes("editorial hero image placeholder:") ? "" : match
  );
}

// Posts that use the text byline ("By … , Role, SaaSed" + Last updated <time>)
// and the extended Person/publisher schema. Per-slug so other posts are untouched.
const TEXT_BYLINE_SLUGS = new Set(["who-inside-salesforce-approves-your-deal"]);
// Per-post social/schema image when the visible image lives in the article body.
const SOCIAL_IMAGE_BY_SLUG: Record<string, string> = {
  "who-inside-salesforce-approves-your-deal": dealApprovalImage.url,
};

// Decode the HTML entities that appear in stored article markup so schema
// text matches the visibly rendered text exactly.
function decodeEntities(s: string): string {
  return s
    .replace(/&#(\d+);/g, (_m, n: string) => String.fromCodePoint(Number(n)))
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

// Extract FAQ Q&A pairs from article HTML. A FAQ section is a heading (h2 or
// h3) reading "Frequently asked questions", "FAQ" or "FAQ – Frequently Asked
// Questions". Two item formats are supported:
//   (a) <h3>Question?</h3> followed by its answer paragraph(s), until the
//       next h3 or h2;
//   (b) <p><strong>Question?</strong> Answer…</p> — a paragraph that starts
//       with a bold question ending in "?" and continues with the answer.
// Question and answer texts are the visible words exactly: tags (including
// links) are dropped, entities decoded, whitespace collapsed. Returns null
// when the article has no such section.
function extractFaqPairs(html: string): { q: string; a: string }[] | null {
  const clean = (s: string) =>
    decodeEntities(s.replace(/<[^>]+>/g, "")).replace(/\s+/g, " ").trim();
  // Locate the FAQ heading among the article's h2/h3 headings.
  let section: string | null = null;
  let headingLevel = 2;
  const headingRegex = /<h([23])\b[^>]*>([\s\S]*?)<\/h\1>/gi;
  let h: RegExpExecArray | null;
  while ((h = headingRegex.exec(html)) !== null) {
    const headingText = clean(h[2]);
    if (/frequently\s+asked\s+questions/i.test(headingText) || /^faq\b/i.test(headingText)) {
      section = html.slice(h.index + h[0].length);
      headingLevel = Number(h[1]);
      break;
    }
  }
  if (section === null) return null;
  const pairs: { q: string; a: string }[] = [];
  // The section ends at the next heading of the same or a higher level.
  const endRe = headingLevel === 2 ? /<h2\b/i : /<h[23]\b/i;
  const end = section.search(endRe);
  const block = end === -1 ? section : section.slice(0, end);
  // Format (a): <h3>Question</h3> followed by answer paragraph(s).
  if (/^\s*<h3\b/i.test(block)) {
    const qa = /<h3\b[^>]*>([\s\S]*?)<\/h3>((?:\s*<p\b[^>]*>[\s\S]*?<\/p>)+)/gi;
    let x: RegExpExecArray | null;
    while ((x = qa.exec(block)) !== null) {
      const answers = [...x[2].matchAll(/<p\b[^>]*>([\s\S]*?)<\/p>/gi)].map((p) => clean(p[1]));
      pairs.push({ q: clean(x[1]), a: answers.join("\n\n") });
    }
    return pairs.length ? pairs : null;
  }
  // Format (b): paragraphs starting with a bold question ending in "?".
  // The answer either continues in the same paragraph or, when the paragraph
  // is only the bold question (markdown articles), is the next paragraph.
  // Leading non-question paragraphs (e.g. a section intro) are skipped;
  // collection stops at the first non-matching paragraph after that.
  const pRegex = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
  let m: RegExpExecArray | null;
  let pendingQ: string | null = null;
  while ((m = pRegex.exec(block)) !== null) {
    const inner = m[1].trim();
    const strong = /^<strong\b[^>]*>([\s\S]*?)<\/strong>\s*([\s\S]*)$/.exec(inner);
    const q = strong ? clean(strong[1]) : "";
    if (strong && q.endsWith("?")) {
      const rest = clean(strong[2]);
      if (rest) {
        pairs.push({ q, a: rest });
        pendingQ = null;
      } else {
        pendingQ = q;
      }
      continue;
    }
    if (pendingQ) {
      pairs.push({ q: pendingQ, a: clean(inner) });
      pendingQ = null;
      continue;
    }
    if (pairs.length) break;
    // else: leading intro paragraph, skip
  }
  return pairs.length ? pairs : null;
}



// Tiny markdown renderer: preserves raw inline HTML (so <a> tags from the
// webhook survive), then handles ##/###, **bold**, *em*, [text](url), bare URLs, and lists.
function renderMarkdown(src: string): string {
  const lines = src.split("\n");
  const out: string[] = [];
  let para: string[] = [];
  let list: string[] | null = null;
  let listType: "ul" | "ol" | null = null;

  const inline = (s: string) =>
    s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/(^|[^*])\*(?!\s)([^*]+?)\*(?!\*)/g, "$1<em>$2</em>")
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      )
      // Auto-link bare URLs not already inside an href="..."
      .replace(
        /(^|[\s(])((?:https?:\/\/)[^\s<)]+)/g,
        '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>',
      );

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${inline(para.join(" "))}</p>`);
      para = [];
    }
  };
  const flushList = () => {
    if (list && listType) {
      out.push(`<${listType}>${list.map((l) => `<li>${inline(l)}</li>`).join("")}</${listType}>`);
      list = null;
      listType = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    if (line.startsWith("### ")) { flushPara(); flushList(); out.push(`<h3>${inline(line.slice(4))}</h3>`); continue; }
    if (line.startsWith("## ")) { flushPara(); flushList(); out.push(`<h2>${inline(line.slice(3))}</h2>`); continue; }
    if (/^\d+\.\s/.test(line)) { flushPara(); if (listType !== "ol") { flushList(); list = []; listType = "ol"; } list!.push(line.replace(/^\d+\.\s/, "")); continue; }
    if (line.startsWith("- ")) { flushPara(); if (listType !== "ul") { flushList(); list = []; listType = "ul"; } list!.push(line.slice(2)); continue; }
    flushList();
    para.push(line);
  }
  flushPara(); flushList();
  return out.join("\n");
}
